// =====================================================================================
// INVRT Radar — web console — account — delivery — webhook endpoints
//
// docs/API-CONTRACTS-C1.md §7 (api/routes/visitor.ts): list, add, test, pause or resume,
// rotate the signing secret, delete, and the delivery history drawer.
//
// SECRETS. The server generates the signing secret and returns it exactly once — on
// create and on rotate — and the column is unreadable to a tenant from then on. That one
// showing is the OneTimeSecretCard at the top of this section; the list is re-read from
// the server and never carries it.
//
// TEST SENDS are synchronous and zero-rated: the server posts ONE signed sample event
// marked `test: true` and reports what the endpoint did. The answer is a RESULT, not an
// error (a receiver answering 500 is exactly what the tenant is trying to find out), so
// it is shown inline under the endpoint, including the server's own sentence when no HTTP
// answer came back at all. No delivery row is written for it.
//
// STATUS. The wire values are `active` | `disabled`; the console calls the latter
// "Paused", since the tenant pauses and resumes it themselves with one PATCH.
// =====================================================================================

import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useApiClient } from "@/hooks/useApi";
import { useSession } from "@/auth/SessionProvider";
import { EmptyState, ErrorState, LoadingState } from "@/components/States";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/toast";
import { formatDateTime } from "@/lib/format";
import { cn } from "@/lib/utils";
import type {
  CreateWebhookRequest,
  CreateWebhookResponse,
  DeleteWebhookResponse,
  RotateWebhookSecretResponse,
  UpdateWebhookRequest,
  WebhookEndpoint,
  WebhookEventKind,
} from "@/lib/types.c1";
import { EventKindBadges, WebhookStatusBadge } from "./badges";
import { DeliveriesDrawer } from "./DeliveriesDrawer";
import { EventKindCheckboxes } from "./EventKindCheckboxes";
import {
  DEFAULT_WEBHOOK_EVENTS,
  useWebhooks,
  webhooksKey,
  type WebhookTestOutcome,
} from "./hooks";
import { OneTimeSecretCard } from "./OneTimeSecretCard";

interface IssuedSecret {
  secret: string;
  url: string;
}

// ---------------------------------------------------------------------------------------
// Add
// ---------------------------------------------------------------------------------------

function AddEndpointDialog({
  onClose,
  onIssued,
}: {
  onClose: () => void;
  onIssued: (issued: IssuedSecret) => void;
}) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const { orgId } = useSession();
  const { push } = useToast();
  const [url, setUrl] = useState("");
  const [events, setEvents] = useState<WebhookEventKind[]>(DEFAULT_WEBHOOK_EVENTS);

  const create = useMutation({
    mutationFn: (body: CreateWebhookRequest) => api.post<CreateWebhookResponse>("/webhooks", body),
    onSuccess: (response) => {
      void queryClient.invalidateQueries({ queryKey: webhooksKey(orgId) });
      if (response.secret) {
        onIssued({ secret: response.secret, url: response.url });
      } else {
        push({ tone: "success", title: "Endpoint added", body: response.url });
      }
      onClose();
    },
  });

  const address = url.trim();
  const valid = /^https:\/\/\S+$/i.test(address) && events.length > 0;

  return (
    <Dialog
      open
      onClose={onClose}
      title="Add a webhook endpoint"
      description="Radar posts each event as signed JSON. The signing secret is shown once, on the next screen."
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={create.isPending}>
            Cancel
          </Button>
          <Button
            onClick={() => create.mutate({ url: address, events })}
            loading={create.isPending}
            disabled={!valid}
          >
            Add endpoint
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Input
          label="Endpoint URL"
          type="url"
          value={url}
          onChange={(event) => setUrl(event.target.value)}
          placeholder="https://example.com/radar/events"
          hint="Must start with https://. Radar verifies nothing about the address until the first send, so use Test once it is added."
        />
        <EventKindCheckboxes
          legend="Events to send"
          hint="Tick at least one."
          value={events}
          onChange={setEvents}
        />
        {create.isError ? <ErrorState error={create.error} /> : null}
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------------------
// Rotate secret / delete
// ---------------------------------------------------------------------------------------

function RotateSecretDialog({
  endpoint,
  onClose,
  onIssued,
}: {
  endpoint: WebhookEndpoint;
  onClose: () => void;
  onIssued: (issued: IssuedSecret) => void;
}) {
  const api = useApiClient();

  const rotate = useMutation({
    mutationFn: () =>
      api.post<RotateWebhookSecretResponse>(`/webhooks/${endpoint.id}/rotate-secret`),
    onSuccess: (response) => {
      onIssued({ secret: response.secret, url: endpoint.url });
      onClose();
    },
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title="Rotate the signing secret?"
      description={endpoint.url}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={rotate.isPending}>
            Cancel
          </Button>
          <Button onClick={() => rotate.mutate()} loading={rotate.isPending}>
            Rotate secret
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3 text-ink-muted">
        <p>
          A fresh secret replaces the current one at once, and every later delivery is
          signed with it. Update your receiver straight away: until you do, its signature
          checks fail. The new secret is shown once on the next screen.
        </p>
        {rotate.isError ? <ErrorState error={rotate.error} /> : null}
      </div>
    </Dialog>
  );
}

function DeleteEndpointDialog({
  endpoint,
  onClose,
}: {
  endpoint: WebhookEndpoint;
  onClose: () => void;
}) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const { orgId } = useSession();
  const { push } = useToast();

  const remove = useMutation({
    mutationFn: () => api.delete<DeleteWebhookResponse>(`/webhooks/${endpoint.id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: webhooksKey(orgId) });
      push({ tone: "success", title: "Endpoint deleted", body: endpoint.url });
      onClose();
    },
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title="Delete this endpoint?"
      description={endpoint.url}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={remove.isPending}>
            Cancel
          </Button>
          <Button variant="danger" onClick={() => remove.mutate()} loading={remove.isPending}>
            Delete endpoint
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3 text-ink-muted">
        <p>
          Radar stops sending to this address at once and its delivery history goes with
          it. If you only want a break, pause it instead.
        </p>
        {remove.isError ? <ErrorState error={remove.error} /> : null}
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------------------
// One row
// ---------------------------------------------------------------------------------------

function describeOutcome(outcome: WebhookTestOutcome): string {
  const duration = `${outcome.duration_ms.toLocaleString("en-GB")} ms`;
  if (outcome.status_code === null) {
    return `Failed: ${outcome.error ?? "No HTTP answer arrived."} (${duration})`;
  }
  return `${outcome.ok ? "Delivered" : "Failed"}: HTTP ${outcome.status_code} in ${duration}`;
}

function EndpointRow({
  endpoint,
  isAdmin,
  outcome,
  testing,
  onTest,
  onToggle,
  toggling,
  onRotate,
  onDelete,
  onDeliveries,
}: {
  endpoint: WebhookEndpoint;
  isAdmin: boolean;
  outcome: WebhookTestOutcome | null;
  testing: boolean;
  onTest: () => void;
  onToggle: () => void;
  toggling: boolean;
  onRotate: () => void;
  onDelete: () => void;
  onDeliveries: () => void;
}) {
  const active = endpoint.status === "active";
  return (
    <TableRow>
      <TableCell className="max-w-xs">
        <p className="break-all font-mono text-xs text-ink">{endpoint.url}</p>
        {outcome ? (
          <p
            className={cn("mt-1 text-xs", outcome.ok ? "text-accent-700" : "text-danger")}
            role="status"
          >
            {describeOutcome(outcome)}
          </p>
        ) : null}
      </TableCell>
      <TableCell>
        <EventKindBadges events={endpoint.events} />
      </TableCell>
      <TableCell>
        <WebhookStatusBadge status={endpoint.status} />
      </TableCell>
      <TableCell className="whitespace-nowrap text-ink-muted">
        {formatDateTime(endpoint.created_at)}
      </TableCell>
      <TableCell className="text-right">
        <span className="flex flex-wrap justify-end gap-2">
          <Button
            variant="secondary"
            size="sm"
            aria-label={`Deliveries for ${endpoint.url}`}
            onClick={onDeliveries}
          >
            Deliveries
          </Button>
          {isAdmin ? (
            <>
              <Button
                variant="secondary"
                size="sm"
                aria-label={`Test ${endpoint.url}`}
                onClick={onTest}
                loading={testing}
              >
                Test
              </Button>
              <Button
                variant="secondary"
                size="sm"
                aria-label={`${active ? "Pause" : "Resume"} ${endpoint.url}`}
                onClick={onToggle}
                loading={toggling}
              >
                {active ? "Pause" : "Resume"}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                aria-label={`Rotate secret for ${endpoint.url}`}
                onClick={onRotate}
              >
                Rotate secret
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-danger hover:text-danger"
                aria-label={`Delete ${endpoint.url}`}
                onClick={onDelete}
              >
                Delete
              </Button>
            </>
          ) : null}
        </span>
      </TableCell>
    </TableRow>
  );
}

// ---------------------------------------------------------------------------------------
// The section
// ---------------------------------------------------------------------------------------

export function WebhooksSection({ isAdmin }: { isAdmin: boolean }) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const { orgId } = useSession();
  const webhooks = useWebhooks();

  const [adding, setAdding] = useState(false);
  const [rotating, setRotating] = useState<WebhookEndpoint | null>(null);
  const [deleting, setDeleting] = useState<WebhookEndpoint | null>(null);
  const [viewing, setViewing] = useState<WebhookEndpoint | null>(null);
  const [issued, setIssued] = useState<IssuedSecret | null>(null);
  const [outcomes, setOutcomes] = useState<Record<string, WebhookTestOutcome>>({});

  const test = useMutation({
    mutationFn: (endpoint: WebhookEndpoint) =>
      api.post<WebhookTestOutcome>(`/webhooks/${endpoint.id}/test`),
    onSuccess: (outcome, endpoint) => {
      setOutcomes((current) => ({ ...current, [endpoint.id]: outcome }));
    },
  });

  const toggle = useMutation({
    mutationFn: (endpoint: WebhookEndpoint) => {
      const body: UpdateWebhookRequest = {
        status: endpoint.status === "active" ? "disabled" : "active",
      };
      return api.patch<WebhookEndpoint>(`/webhooks/${endpoint.id}`, body);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: webhooksKey(orgId) });
    },
  });

  return (
    <section aria-label="Webhook endpoints" className="flex flex-col gap-4">
      {issued ? (
        <OneTimeSecretCard
          title="Copy your signing secret now"
          noun="secret"
          secret={issued.secret}
          subject={issued.url}
          onDone={() => setIssued(null)}
        />
      ) : null}

      <Card>
        <CardHeader className="flex-row flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle>Webhook endpoints</CardTitle>
            <CardDescription className="mt-1">
              Radar posts each event to your own HTTPS address as JSON, signed with the
              endpoint&rsquo;s secret in the X-Radar-Signature header. Failed deliveries are
              retried, then parked until you redeliver them.
            </CardDescription>
          </div>
          {isAdmin ? (
            <Button onClick={() => setAdding(true)}>
              <Plus className="h-4 w-4" aria-hidden="true" />
              Add endpoint
            </Button>
          ) : null}
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          {test.isError ? <ErrorState error={test.error} /> : null}
          {toggle.isError ? <ErrorState error={toggle.error} /> : null}

          {webhooks.isLoading ? <LoadingState label="Loading your endpoints…" /> : null}
          {webhooks.isError ? (
            <ErrorState error={webhooks.error} onRetry={() => void webhooks.refetch()} />
          ) : null}

          {webhooks.isSuccess ? (
            webhooks.data.webhooks.length === 0 ? (
              <EmptyState
                title="No webhook endpoints yet"
                body={
                  isAdmin
                    ? "Add an HTTPS address and Radar sends each identified visitor or revealed contact to it as it happens."
                    : "An administrator on your team can add one. Endpoints your organisation has set up appear here."
                }
                {...(isAdmin
                  ? { action: <Button onClick={() => setAdding(true)}>Add endpoint</Button> }
                  : {})}
              />
            ) : (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>Endpoint</TableHeaderCell>
                    <TableHeaderCell>Events</TableHeaderCell>
                    <TableHeaderCell>Status</TableHeaderCell>
                    <TableHeaderCell>Created</TableHeaderCell>
                    <TableHeaderCell className="text-right">Actions</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {webhooks.data.webhooks.map((endpoint) => (
                    <EndpointRow
                      key={endpoint.id}
                      endpoint={endpoint}
                      isAdmin={isAdmin}
                      outcome={outcomes[endpoint.id] ?? null}
                      testing={test.isPending && test.variables?.id === endpoint.id}
                      onTest={() => test.mutate(endpoint)}
                      toggling={toggle.isPending && toggle.variables?.id === endpoint.id}
                      onToggle={() => toggle.mutate(endpoint)}
                      onRotate={() => setRotating(endpoint)}
                      onDelete={() => setDeleting(endpoint)}
                      onDeliveries={() => setViewing(endpoint)}
                    />
                  ))}
                </TableBody>
              </Table>
            )
          ) : null}
        </CardContent>
      </Card>

      {adding ? <AddEndpointDialog onClose={() => setAdding(false)} onIssued={setIssued} /> : null}
      {rotating ? (
        <RotateSecretDialog
          endpoint={rotating}
          onClose={() => setRotating(null)}
          onIssued={setIssued}
        />
      ) : null}
      {deleting ? (
        <DeleteEndpointDialog endpoint={deleting} onClose={() => setDeleting(null)} />
      ) : null}
      {viewing ? (
        <DeliveriesDrawer endpoint={viewing} isAdmin={isAdmin} onClose={() => setViewing(null)} />
      ) : null}
    </section>
  );
}
